import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Background, MarkerType, MiniMap, ReactFlow, useEdgesState, useNodesState, type Edge, type Node, type ReactFlowInstance } from '@xyflow/react';
import { EyeOff, Focus, GitBranch, LocateFixed, Map as MapIcon, Network, RotateCcw, Route, ScanSearch } from 'lucide-react';
import { FsmMiniViewer } from './canvas/FsmMiniViewer';
import { signalEdgeTypes, type SignalEdgeData } from './canvas/edges/SignalEdge';
import { layoutWithElk, provisionalNodes } from './canvas/layout/elkLayout';
import { hardwareNodeTypes } from './canvas/nodes/HardwareNodes';
import type { HardwareEdge, HardwareGraph, HardwareNode, HardwareNodeViewModel, NodeKind, SourceRange } from './types';

type FlowNode = Node<HardwareNodeViewModel>;
type FlowEdge = Edge<SignalEdgeData, 'signal'>;

function outputHandle(node?: HardwareNode) {
  if (!node) return 'output';
  if (node.type === 'register') return 'q';
  if (node.type === 'input' || node.type === 'constant') return 'out';
  return 'y';
}

function edgeViews(graph: HardwareGraph): FlowEdge[] {
  const nodes = new Map([...graph.ports, ...graph.nodes].map((node) => [node.id, node]));
  const inputIndexes = new Map<string, number>();
  return graph.edges.map((edge) => {
    const target = nodes.get(edge.target);
    let targetHandle = 'input';
    if (edge.kind === 'clock') targetHandle = 'clk';
    else if (edge.kind === 'reset') targetHandle = 'rst';
    else if (edge.kind === 'control') targetHandle = target?.type === 'mux' ? 'sel' : 'en';
    else if (target?.type === 'register') targetHandle = 'd';
    else if (target?.type === 'output') targetHandle = 'in';
    else {
      const index = inputIndexes.get(edge.target) ?? 0;
      targetHandle = target?.type === 'mux' || target?.type === 'logic' ? `in-${index}` : index === 0 ? 'a' : 'b';
      inputIndexes.set(edge.target, index + 1);
    }
    return edgeView(edge, outputHandle(nodes.get(edge.source)), targetHandle);
  });
}

function edgeView(edge: HardwareEdge, sourceHandle?: string, targetHandle?: string): FlowEdge {
  return {
    id: edge.id,
    source: edge.source,
    target: edge.target,
    label: edge.signal,
    type: 'signal',
    sourceHandle,
    targetHandle,
    data: { ir: edge, isTraced: false, isDimmed: false, isLocal: false },
    markerEnd: { type: MarkerType.ArrowClosed, width: 13, height: 13 },
  };
}

export function adaptGraph(graph: HardwareGraph): { nodes: FlowNode[]; edges: FlowEdge[] } {
  return { nodes: provisionalNodes(graph), edges: edgeViews(graph) };
}

function walkEdges(graph: HardwareGraph, nodeId: string, direction: 'forward' | 'backward') {
  const ids = new Set<string>();
  const queue = [nodeId];
  const visited = new Set<string>();
  while (queue.length) {
    const current = queue.shift()!;
    if (visited.has(current)) continue;
    visited.add(current);
    graph.edges.forEach((edge) => {
      const match = direction === 'forward' ? edge.source === current : edge.target === current;
      if (match) { ids.add(edge.id); queue.push(direction === 'forward' ? edge.target : edge.source); }
    });
  }
  return ids;
}

export function probeNodeEdges(graph: HardwareGraph, nodeId: string) {
  return [...new Set([...walkEdges(graph, nodeId, 'backward'), ...walkEdges(graph, nodeId, 'forward')])];
}

export function probeSignalEdges(graph: HardwareGraph, edge: HardwareEdge) {
  return [...new Set([edge.id, ...walkEdges(graph, edge.source, 'backward'), ...walkEdges(graph, edge.target, 'forward')])];
}

export function keyboardNeighbor(graph: HardwareGraph, nodeId: string, key: 'ArrowLeft' | 'ArrowRight' | 'ArrowUp' | 'ArrowDown') {
  const allNodes = [...graph.ports, ...graph.nodes];
  const incoming = graph.edges.filter((edge) => edge.target === nodeId).map((edge) => edge.source);
  const outgoing = graph.edges.filter((edge) => edge.source === nodeId).map((edge) => edge.target);
  if (key === 'ArrowLeft') return incoming[0];
  if (key === 'ArrowRight') return outgoing[0];
  const connected = [...new Set([...incoming, ...outgoing])];
  if (connected.length) return key === 'ArrowUp' ? connected.at(-1) : connected[0];
  const index = allNodes.findIndex((node) => node.id === nodeId);
  const offset = key === 'ArrowUp' ? -1 : 1;
  return allNodes[(index + offset + allNodes.length) % allNodes.length]?.id;
}

function nodeColor(kind: NodeKind) {
  const colors: Partial<Record<NodeKind, string>> = {
    input: '#475569', output: '#38bdf8', register: '#6366f1', arithmetic: '#38bdf8', mux: '#fbbf24',
    logic: '#64748b', comparison: '#22c55e', constant: '#475569', fsm: '#a855f7', memory: '#818cf8', module: '#94a3b8',
  };
  return colors[kind] ?? '#475569';
}

interface CanvasProps {
  graph: HardwareGraph;
  onSelectNode: (node?: HardwareNode) => void;
  onSelectEdge?: (edge?: HardwareEdge) => void;
  onTraceEdges?: (ids: string[]) => void;
  onFocusSource?: (range: SourceRange) => void;
  traceEdgeIds?: string[];
  search?: string;
  layoutRevision?: number;
  focusNodeId?: string;
}

type NetworkFocus = 'all' | 'data' | 'clock' | 'reset' | 'control';

export function HardwareCanvas({ graph, onSelectNode, onSelectEdge, onTraceEdges, onFocusSource, traceEdgeIds = [], search = '', layoutRevision = 0, focusNodeId }: CanvasProps) {
  const initial = useMemo(() => adaptGraph(graph), [graph]);
  const [nodes, setNodes, onNodesChange] = useNodesState<FlowNode>(initial.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState<FlowEdge>(initial.edges);
  const [instance, setInstance] = useState<ReactFlowInstance<FlowNode, FlowEdge>>();
  const [zoom, setZoom] = useState(100);
  const [showMinimap, setShowMinimap] = useState(true);
  const [showFsmViewer, setShowFsmViewer] = useState(true);
  const [networkFocus, setNetworkFocus] = useState<NetworkFocus>('all');
  const [hoveredNodeId, setHoveredNodeId] = useState<string>();
  const [hiddenIds, setHiddenIds] = useState<string[]>([]);
  const [context, setContext] = useState<{ x: number; y: number; node: HardwareNodeViewModel }>();
  const [keyboardAnnouncement, setKeyboardAnnouncement] = useState('Tab to enter the schematic. Use arrow keys to follow connected hardware.');
  const canvasRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let live = true;
    setNodes(provisionalNodes(graph));
    setEdges(edgeViews(graph));
    void layoutWithElk(graph).then((layoutNodes) => {
      if (!live) return;
      setNodes(layoutNodes);
      requestAnimationFrame(() => void instance?.fitView({ padding: 0.16, duration: 260 }));
    });
    return () => { live = false; };
  }, [graph, layoutRevision, instance, setEdges, setNodes]);

  const tracedNodes = useMemo(() => {
    const ids = new Set<string>();
    graph.edges.forEach((edge) => { if (traceEdgeIds.includes(edge.id)) { ids.add(edge.source); ids.add(edge.target); } });
    return ids;
  }, [graph.edges, traceEdgeIds]);
  const traceActive = traceEdgeIds.length > 0;

  const focusedNetworkNodes = useMemo(() => {
    if (networkFocus === 'all') return new Set<string>();
    const ids = new Set<string>();
    graph.edges.forEach((edge) => {
      if ((edge.kind ?? 'data') === networkFocus) { ids.add(edge.source); ids.add(edge.target); }
    });
    return ids;
  }, [graph.edges, networkFocus]);

  const searchTextByNode = useMemo(() => {
    const text = new Map<string, string>();
    [...graph.ports, ...graph.nodes].forEach((node) => text.set(node.id, `${node.id} ${node.label} ${node.type}`.toLowerCase()));
    graph.edges.forEach((edge) => {
      const edgeText = `${edge.signal} ${edge.kind ?? 'data'}`.toLowerCase();
      text.set(edge.source, `${text.get(edge.source) ?? ''} ${edgeText}`);
      text.set(edge.target, `${text.get(edge.target) ?? ''} ${edgeText}`);
    });
    return text;
  }, [graph]);

  const visibleNodes = useMemo(() => nodes.filter((node) => !hiddenIds.includes(node.id)).map((node) => {
    const match = Boolean(search && searchTextByNode.get(node.id)?.includes(search.toLowerCase()));
    const dimmed = (traceActive && !tracedNodes.has(node.id)) || (networkFocus !== 'all' && !focusedNetworkNodes.has(node.id));
    return { ...node, className: [match ? 'search-match' : '', focusNodeId === node.id ? 'source-focus-match' : '', dimmed ? 'trace-dimmed' : ''].filter(Boolean).join(' ') };
  }), [focusNodeId, focusedNetworkNodes, hiddenIds, networkFocus, nodes, search, searchTextByNode, traceActive, tracedNodes]);

  const decoratedEdges = useMemo(() => edges.filter((edge) => !hiddenIds.includes(edge.source) && !hiddenIds.includes(edge.target)).map((edge) => {
    const traced = traceEdgeIds.includes(edge.id);
    const local = hoveredNodeId === edge.source || hoveredNodeId === edge.target;
    const networkDimmed = networkFocus !== 'all' && (edge.data?.ir.kind ?? 'data') !== networkFocus;
    return { ...edge, data: { ...edge.data!, isTraced: traced, isDimmed: (traceActive && !traced) || networkDimmed, isLocal: local } };
  }), [edges, hiddenIds, hoveredNodeId, networkFocus, traceActive, traceEdgeIds]);

  const fit = useCallback(() => void instance?.fitView({ padding: 0.16, duration: 220 }), [instance]);
  const resetCanvas = () => { setHiddenIds([]); setNetworkFocus('all'); setContext(undefined); onTraceEdges?.([]); requestAnimationFrame(fit); };

  useEffect(() => {
    if (!focusNodeId || hiddenIds.includes(focusNodeId)) return;
    setNodes((items) => items.map((node) => ({ ...node, selected: node.id === focusNodeId })));
    instance?.fitView({ nodes: [{ id: focusNodeId }], padding: 1.45, duration: 180 });
  }, [focusNodeId, hiddenIds, instance, setNodes]);

  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (target.closest('.monaco-editor, input, textarea, select')) return;
      const focusedNode = target.closest<HTMLElement>('.react-flow__node');
      if (focusedNode && ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
        const currentId = focusedNode.dataset.id;
        const nextId = currentId ? keyboardNeighbor(graph, currentId, event.key as 'ArrowLeft' | 'ArrowRight' | 'ArrowUp' | 'ArrowDown') : undefined;
        const nextNode = [...graph.ports, ...graph.nodes].find((node) => node.id === nextId);
        if (nextId && nextNode && !hiddenIds.includes(nextId)) {
          event.preventDefault();
          setNodes((items) => items.map((node) => ({ ...node, selected: node.id === nextId })));
          onSelectEdge?.(); onSelectNode(nextNode); onTraceEdges?.(probeNodeEdges(graph, nextId));
          const incoming = graph.edges.filter((edge) => edge.target === nextId).length;
          const outgoing = graph.edges.filter((edge) => edge.source === nextId).length;
          setKeyboardAnnouncement(`${nextNode.type} ${nextNode.label}, ${nextNode.width}-bit, ${incoming} inputs, ${outgoing} outputs.`);
          instance?.fitView({ nodes: [{ id: nextId }], padding: 1.5, duration: 160 });
          requestAnimationFrame(() => {
            const element = [...(canvasRef.current?.querySelectorAll<HTMLElement>('.react-flow__node') ?? [])].find((item) => item.dataset.id === nextId);
            element?.focus();
          });
        }
        return;
      }
      if (event.key.toLowerCase() === 'f') { event.preventDefault(); fit(); }
      if (event.key === 'Escape') { setContext(undefined); onSelectNode(); onSelectEdge?.(); onTraceEdges?.([]); }
    };
    addEventListener('keydown', key);
    return () => removeEventListener('keydown', key);
  }, [fit, graph, hiddenIds, instance, onSelectEdge, onSelectNode, onTraceEdges, setNodes]);

  const edgeIdsFromNode = (nodeId: string, direction: 'forward' | 'backward') => {
    return walkEdges(graph, nodeId, direction);
  };

  const traceFromNode = (nodeId: string, direction: 'forward' | 'backward') => {
    onTraceEdges?.([...edgeIdsFromNode(nodeId, direction)]);
    setContext(undefined);
  };

  const probeNode = (nodeId: string) => {
    onTraceEdges?.(probeNodeEdges(graph, nodeId));
  };

  const probeEdge = (edge: HardwareEdge) => {
    onTraceEdges?.(probeSignalEdges(graph, edge));
  };

  return <div className="hardware-canvas" ref={canvasRef} onContextMenu={(event) => event.preventDefault()} onFocusCapture={(event) => {
    const element = (event.target as HTMLElement).closest<HTMLElement>('.react-flow__node');
    const node = [...graph.ports, ...graph.nodes].find((item) => item.id === element?.dataset.id);
    if (node) {
      onSelectEdge?.(); onSelectNode(node);
      const incoming = graph.edges.filter((edge) => edge.target === node.id).length;
      const outgoing = graph.edges.filter((edge) => edge.source === node.id).length;
      setKeyboardAnnouncement(`${node.type} ${node.label}, ${node.width}-bit, ${incoming} inputs, ${outgoing} outputs.`);
    }
  }}>
    <div className="sr-only" aria-live="polite">{keyboardAnnouncement}</div>
    <div className="canvas-floating-toolbar" aria-label="Schematic controls">
      <span className="zoom-readout">{zoom}%</span>
      <button data-tooltip="Fit view (F)" aria-label="Fit schematic" onClick={fit}><ScanSearch size={13} /> Fit</button>
      <button data-tooltip="Run ELK auto layout (A)" aria-label="Auto layout" onClick={() => { setHiddenIds([]); void layoutWithElk(graph).then((layoutNodes) => { setNodes(layoutNodes); requestAnimationFrame(fit); }); }}><Route size={13} /> Auto layout</button>
      <span className="detail-readout"><Network size={12} /> RTL</span>
      <div className="network-isolation" role="group" aria-label="Network isolation view">
        {(['all', 'data', 'clock', 'reset', 'control'] as const).map((kind) => <button key={kind} className={networkFocus === kind ? 'is-active' : ''} data-tooltip={`Show ${kind === 'all' ? 'all networks' : `${kind} network only`}`} aria-label={`Show ${kind === 'all' ? 'all networks' : `${kind} network only`}`} aria-pressed={networkFocus === kind} onClick={() => setNetworkFocus(kind)}>{kind === 'all' ? 'ALL' : kind === 'control' ? 'CTRL' : kind.toUpperCase()}</button>)}
      </div>
      <button className={showMinimap ? 'is-active' : ''} data-tooltip="Toggle minimap" aria-label="Toggle minimap" onClick={() => setShowMinimap((value) => !value)}><MapIcon size={13} /></button>
      {!!graph.fsms?.length && <button className={showFsmViewer ? 'is-active' : ''} data-tooltip="Toggle state viewer" aria-label="Toggle FSM state viewer" onClick={() => setShowFsmViewer((value) => !value)}><GitBranch size={13} /></button>}
      <button data-tooltip="Reset canvas" aria-label="Reset canvas" onClick={resetCanvas}><RotateCcw size={13} /></button>
    </div>
    <div className="keyboard-help"><kbd>Tab</kbd> nodes <kbd>←</kbd> fan-in <kbd>→</kbd> fan-out</div>
    <ReactFlow<FlowNode, FlowEdge>
      nodes={visibleNodes}
      edges={decoratedEdges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      nodeTypes={hardwareNodeTypes}
      edgeTypes={signalEdgeTypes}
      onInit={setInstance}
      fitView
      fitViewOptions={{ padding: 0.16 }}
      minZoom={0.25}
      maxZoom={2.2}
      selectionOnDrag
      multiSelectionKeyCode="Shift"
      panActivationKeyCode={null}
      nodesFocusable
      edgesFocusable={false}
      autoPanOnNodeFocus
      onMoveEnd={(_, viewport) => setZoom(Math.round(viewport.zoom * 100))}
      onNodeMouseEnter={(_, node) => setHoveredNodeId(node.id)}
      onNodeMouseLeave={() => setHoveredNodeId(undefined)}
      onNodeClick={(_, node) => { setContext(undefined); onSelectEdge?.(); onSelectNode(node.data.ir); probeNode(node.id); }}
      onEdgeClick={(_, edge) => { setContext(undefined); onSelectNode(); onSelectEdge?.(edge.data?.ir); if (edge.data?.ir) probeEdge(edge.data.ir); }}
      onPaneClick={() => { setContext(undefined); onSelectNode(); onSelectEdge?.(); onTraceEdges?.([]); }}
      onNodeContextMenu={(event, node) => {
        const rect = canvasRef.current?.getBoundingClientRect();
        setContext({ x: event.clientX - (rect?.left ?? 0), y: event.clientY - (rect?.top ?? 0), node: node.data });
      }}
      attributionPosition="bottom-right"
    >
      <Background color="#2d3748" gap={22} size={1} />
      {showMinimap && <MiniMap pannable zoomable nodeColor={(node) => nodeColor((node.data as HardwareNodeViewModel).kind)} maskColor="rgba(18,22,26,.82)" />}
    </ReactFlow>
    {showFsmViewer && graph.fsms?.[0] && <FsmMiniViewer fsm={graph.fsms[0]} close={() => setShowFsmViewer(false)} />}
    {context && <div className="node-context-menu" style={{ left: context.x, top: context.y }} role="menu">
      <button onClick={() => { instance?.fitView({ nodes: [{ id: context.node.id }], padding: 1.4, duration: 180 }); setContext(undefined); }}><Focus size={13} /> Focus</button>
      <button onClick={() => traceFromNode(context.node.id, 'backward')}><LocateFixed size={13} /> Trace inputs</button>
      <button onClick={() => traceFromNode(context.node.id, 'forward')}><Route size={13} /> Trace outputs</button>
      {context.node.source && <button onClick={() => { onFocusSource?.(context.node.source!); setContext(undefined); }}><ScanSearch size={13} /> Focus source</button>}
      <button onClick={() => { setHiddenIds((ids) => [...ids, context.node.id]); setContext(undefined); }}><EyeOff size={13} /> Hide node</button>
      <button onClick={() => { void navigator.clipboard.writeText(context.node.id); setContext(undefined); }}><Network size={13} /> Copy node ID</button>
    </div>}
  </div>;
}
